import * as XLSX from 'xlsx';
import { normalizeShopIds } from './shopAssignmentUtils';
import {
  countsAsVardiyaMuduru,
  excelDutyLabelForStaff,
  parseExcelDutyRole,
} from './staffRoleUtils';

const DAY_CODES = ['Pzt', 'Sal', 'Car', 'Per', 'Cum', 'Cmt', 'Paz'];

const DAY_ALIASES = {
  pzt: 'Pzt',
  pazartesi: 'Pzt',
  sal: 'Sal',
  salı: 'Sal',
  sali: 'Sal',
  car: 'Car',
  çar: 'Car',
  çarşamba: 'Car',
  carsamba: 'Car',
  per: 'Per',
  perşembe: 'Per',
  persembe: 'Per',
  cum: 'Cum',
  cuma: 'Cum',
  cmt: 'Cmt',
  cumartesi: 'Cmt',
  paz: 'Paz',
  pazar: 'Paz',
};

const OFF_DAY_MARKERS = new Set([
  'izinli',
  'izin',
  'off',
  'yi',
  'yı',
  'siparis',
  'sipariş',
]);

const HEADER_ALIASES = {
  sube: 'shop',
  şube: 'shop',
  shop: 'shop',
  magaza: 'shop',
  mağaza: 'shop',
  'magaza adi': 'shop',
  'mağaza adı': 'shop',
  hafta: 'week',
  'hafta başlangıç': 'week',
  'hafta baslangic': 'week',
  week: 'week',
  week_start: 'week',
  tarih: 'week',
  gun: 'day',
  gün: 'day',
  day: 'day',
  vardiya: 'slot',
  slot: 'slot',
  gorevi: 'role',
  görevi: 'role',
  baslangic: 'start',
  başlangıç: 'start',
  start: 'start',
  start_time: 'start',
  bitis: 'end',
  bitiş: 'end',
  end: 'end',
  end_time: 'end',
  personel: 'people',
  people: 'people',
  calisan: 'people',
  çalışan: 'people',
  isim: 'people',
  ad: 'people',
};

function cellText(value) {
  if (value == null) return '';
  return String(value).trim();
}

const TR_ASCII_MAP = {
  ç: 'c',
  ğ: 'g',
  ı: 'i',
  ö: 'o',
  ş: 's',
  ü: 'u',
  â: 'a',
  î: 'i',
  û: 'u',
};

function transliterateTurkish(value) {
  return String(value).replace(/[çğıöşüâîû]/g, (ch) => TR_ASCII_MAP[ch] || ch);
}

function normalizeMatchKey(value) {
  return transliterateTurkish(
    cellText(value)
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''),
  )
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeHeader(value) {
  return normalizeMatchKey(value);
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function isoFromParts(y, m, d) {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

function excelDateToIso(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return isoFromParts(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) return isoFromParts(parsed.y, parsed.m, parsed.d);
  }
  const raw = cellText(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (us) {
    let y = Number(us[3]);
    if (y < 100) y += 2000;
    return isoFromParts(y, Number(us[1]), Number(us[2]));
  }
  const tr = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (tr) return isoFromParts(Number(tr[3]), Number(tr[2]), Number(tr[1]));
  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) {
    return isoFromParts(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
  }
  return null;
}

export function mondayIsoFromDate(isoDate) {
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return isoFromParts(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function addDaysIso(isoDate, days) {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setDate(d.getDate() + days);
  return isoFromParts(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function parseTimeValue(value, fallback = null) {
  if (value == null || value === '') return fallback;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${pad2(value.getHours())}:${pad2(value.getMinutes())}`;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const totalMin = Math.round(value * 24 * 60);
    const h = Math.floor(totalMin / 60) % 24;
    const m = totalMin % 60;
    return `${pad2(h)}:${pad2(m)}`;
  }
  const raw = cellText(value);
  const m = raw.match(/^(\d{1,2})[.:](\d{2})$/);
  if (m) return `${pad2(m[1])}:${m[2]}`;
  return fallback;
}

export function parseShiftTimeRange(cell) {
  const raw = cellText(cell);
  if (!raw) return null;
  const norm = normalizeMatchKey(raw);
  if (OFF_DAY_MARKERS.has(norm)) return null;
  if (norm.length <= 3 && !/\d/.test(norm)) return null;

  const m = raw.match(/^(\d{1,2})[.:](\d{2})\s*[-–]\s*(\d{1,2})[.:](\d{2})$/);
  if (!m) return null;
  return {
    start: `${pad2(m[1])}:${m[2]}`,
    end: `${pad2(m[3])}:${m[4]}`,
  };
}

function normalizeDayCode(value) {
  const key = normalizeMatchKey(value);
  if (!key) return null;
  if (DAY_ALIASES[key]) return DAY_ALIASES[key];
  if (DAY_CODES.includes(cellText(value))) return cellText(value);
  return null;
}

function splitPeopleNames(value) {
  return cellText(value)
    .split(/[,;|/]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function shopTokens(name) {
  return normalizeMatchKey(name)
    .split(/\s+/)
    .filter((t) => t.length > 2 && !['mmj', 'magaza', 'magazasi', 'sube', 'store'].includes(t));
}

export function findShopId(shopName, shops) {
  const key = normalizeMatchKey(shopName);
  if (!key) return null;

  const exact = shops.find((s) => normalizeMatchKey(s.shop) === key);
  if (exact) return exact.id;

  const partial = shops.find((s) => {
    const n = normalizeMatchKey(s.shop);
    return n.includes(key) || key.includes(n);
  });
  if (partial) return partial.id;

  const tokens = shopTokens(shopName);
  if (!tokens.length) return null;

  let best = null;
  let bestScore = 0;
  shops.forEach((s) => {
    const shopKey = normalizeMatchKey(s.shop);
    let score = 0;
    tokens.forEach((t) => {
      if (shopKey.includes(t)) score += 1;
    });
    if (score > bestScore) {
      bestScore = score;
      best = s;
    }
  });
  return bestScore > 0 ? best.id : null;
}

function mapHeaders(headerRow) {
  const map = {};
  headerRow.forEach((cell, idx) => {
    const norm = normalizeHeader(cell);
    const field = HEADER_ALIASES[norm];
    if (field && map[field] == null) map[field] = idx;
  });
  return map;
}

function staffAssignedToShop(member, shopId) {
  return normalizeShopIds(member.assigned_shop_ids).includes(String(shopId));
}

export function resolveStaffByName(name, staffList, shopId) {
  const key = normalizeMatchKey(name);
  if (!key) return null;

  const assigned = staffList.filter(
    (s) => s.auth_user_id && staffAssignedToShop(s, shopId),
  );
  const exact = assigned.find((s) => normalizeMatchKey(s.full_name) === key);
  if (exact) return exact;

  const contains = assigned.find((s) => {
    const nm = normalizeMatchKey(s.full_name);
    return nm.includes(key) || key.includes(nm);
  });
  if (contains) return contains;

  const anyExact = staffList.find(
    (s) => s.auth_user_id && normalizeMatchKey(s.full_name) === key,
  );
  return anyExact || null;
}

function addPersonToShift(entry, personName, dutyRole) {
  const nameKey = normalizeMatchKey(personName);
  if (!nameKey) return;
  const existing = entry.peopleEntries.find((p) => normalizeMatchKey(p.name) === nameKey);
  if (existing) {
    if (dutyRole && !existing.dutyRole) existing.dutyRole = dutyRole;
    return;
  }
  entry.peopleEntries.push({ name: personName, dutyRole: dutyRole || null });
}

function finalizeShifts(grouped, staff) {
  const warnings = [];
  const shifts = [...grouped.values()].map((shift) => {
    const assigned_user_ids = [];
    const people = [];
    let vmCount = 0;

    shift.peopleEntries.forEach(({ name, dutyRole }) => {
      const member = resolveStaffByName(name, staff, shift.shopId);
      if (!member) {
        warnings.push({
          message: `${shift.shopName} / ${shift.day} / ${shift.startTime}–${shift.endTime}: "${name}" eşleşmedi`,
        });
        people.push(name);
        if (dutyRole === 'vm') vmCount += 1;
        return;
      }
      if (!staffAssignedToShop(member, shift.shopId)) {
        warnings.push({
          message: `${shift.shopName}: "${name}" bu şubeye atanmamış (yine de eklendi)`,
        });
      }
      if (!assigned_user_ids.includes(member.auth_user_id)) {
        assigned_user_ids.push(member.auth_user_id);
        people.push(cellText(member.full_name));
        if (countsAsVardiyaMuduru({ member, excelDutyRole: dutyRole })) vmCount += 1;
      }
    });

    if (vmCount > 1) {
      warnings.push({
        message: `${shift.shopName} / ${shift.day} / ${shift.startTime}–${shift.endTime}: ${vmCount} vardiya müdürü (VM)`,
      });
    }

    return {
      ...shift,
      slotLabel: `${shift.startTime}–${shift.endTime}`,
      assigned_user_ids,
      people,
    };
  });

  return { shifts, warnings };
}

function isMmjMatrixSheet(rows) {
  return rows.some((row) => {
    const roleCell = normalizeHeader(row[1]);
    if (roleCell === 'gorevi') return true;
    return row.some((cell) => normalizeHeader(cell) === 'gorevi');
  });
}

function extractShopNameFromTitle(rows) {
  const title = cellText(rows[0]?.[1]) || cellText(rows[0]?.[0]);
  if (!title) return '';
  const cleaned = title
    .replace(/MMJ/gi, '')
    .replace(/HAFTALIK/gi, '')
    .replace(/SHIFT/gi, '')
    .replace(/MAĞAZA(SI)?/gi, '')
    .trim();
  return cleaned || title;
}

function resolveShopId({ shopName, shops, rows, fallbackShopId }) {
  const candidates = [
    shopName,
    extractShopNameFromTitle(rows),
  ].filter(Boolean);

  for (const candidate of candidates) {
    const id = findShopId(candidate, shops);
    if (id) return { shopId: id, shopName: candidate, usedFallback: false };
  }

  if (fallbackShopId != null && shops.some((s) => s.id === fallbackShopId)) {
    const shop = shops.find((s) => s.id === fallbackShopId);
    return {
      shopId: fallbackShopId,
      shopName: cellText(shop?.shop) || shopName || 'Seçili şube',
      usedFallback: true,
    };
  }

  return { shopId: null, shopName: candidates[0] || shopName, usedFallback: false };
}

function readMmjMatrixRows(rows) {
  const dutyRowIdx = rows.findIndex((row) => normalizeHeader(row[1]) === 'gorevi');
  if (dutyRowIdx < 0) return null;

  const dutyRow = rows[dutyRowIdx];
  const dateRow = dutyRowIdx > 0 ? rows[dutyRowIdx - 1] : null;

  const dayColumns = [];
  for (let c = 2; c < dutyRow.length; c += 1) {
    const day = normalizeDayCode(dutyRow[c]);
    if (day) dayColumns.push({ col: c, day });
    else if (dayColumns.length >= 7) break;
  }
  if (!dayColumns.length) {
    return {
      errors: [{ row: dutyRowIdx + 1, message: 'Gün sütunları bulunamadı (PAZARTESİ…PAZAR)' }],
      templateGrouped: new Map(),
      dataRows: 0,
      weekStart: null,
    };
  }

  let weekStart = null;
  if (dateRow) {
    const firstDate = excelDateToIso(dateRow[dayColumns[0].col]);
    if (firstDate) weekStart = mondayIsoFromDate(firstDate);
  }
  if (!weekStart) {
    return {
      errors: [{ row: dutyRowIdx, message: 'Hafta tarih satırı bulunamadı (Tarih satırı)' }],
      templateGrouped: new Map(),
      dataRows: 0,
      weekStart: null,
    };
  }

  const templateGrouped = new Map();
  let dataRows = 0;

  for (let r = dutyRowIdx + 1; r < rows.length; r += 1) {
    const row = rows[r] || [];
    const personName = cellText(row[0]);
    const roleLabel = cellText(row[1]);
    if (!personName) continue;
    if (!roleLabel || normalizeHeader(roleLabel) === 'gorevi') continue;
    const dutyRole = parseExcelDutyRole(roleLabel);
    dataRows += 1;

    dayColumns.forEach(({ col, day }) => {
      const range = parseShiftTimeRange(row[col]);
      if (!range) return;
      const key = `${weekStart}|${day}|${range.start}|${range.end}`;
      if (!templateGrouped.has(key)) {
        templateGrouped.set(key, {
          weekStart,
          day,
          startTime: range.start,
          endTime: range.end,
          peopleEntries: [],
        });
      }
      addPersonToShift(templateGrouped.get(key), personName, dutyRole);
    });
  }

  return { templateGrouped, dataRows, errors: [], weekStart, dutyRowIdx };
}

function expandTemplateToAllShops(templateGrouped, shops, staff) {
  const grouped = new Map();
  shops.forEach((shop) => {
    const shopName = cellText(shop.shop) || `Mağaza ${shop.id}`;
    templateGrouped.forEach((entry) => {
      const key = `${shop.id}|${entry.weekStart}|${entry.day}|${entry.startTime}|${entry.endTime}`;
      grouped.set(key, {
        shopId: shop.id,
        shopName,
        weekStart: entry.weekStart,
        day: entry.day,
        startTime: entry.startTime,
        endTime: entry.endTime,
        peopleEntries: entry.peopleEntries.map((p) => ({ ...p })),
      });
    });
  });
  return finalizeShifts(grouped, staff);
}

function parseMmjMatrixSheet(rows, { shops, staff, sheetName, fallbackShopId }) {
  const parsed = readMmjMatrixRows(rows);
  if (!parsed) return null;

  const { templateGrouped, dataRows, errors, weekStart, dutyRowIdx } = parsed;
  if (errors.length) {
    return { shifts: [], warnings: [], errors, dataRows, shopIds: [], weeks: [] };
  }
  if (!templateGrouped.size) {
    return { shifts: [], warnings: [], errors: [], dataRows, shopIds: [], weeks: [] };
  }

  const dutyRow = rows[dutyRowIdx];
  let shopName = cellText(dutyRow[0]);
  if (!shopName || normalizeHeader(shopName) === 'magaza adi') {
    shopName = extractShopNameFromTitle(rows);
  }

  const resolved = resolveShopId({ shopName, shops, rows, fallbackShopId });
  const shopId = resolved.shopId;
  shopName = resolved.shopName;
  const usedFallbackShop = resolved.usedFallback;
  if (!shopId) {
    return {
      shifts: [],
      warnings: [],
      errors: [{ row: dutyRowIdx + 1, message: `Şube bulunamadı: ${shopName || sheetName}` }],
      dataRows: 0,
      shopIds: [],
      weeks: [],
    };
  }

  const resolvedShopName = cellText(shops.find((s) => s.id === shopId)?.shop) || shopName;
  const grouped = new Map();
  templateGrouped.forEach((entry) => {
    const key = `${shopId}|${entry.weekStart}|${entry.day}|${entry.startTime}|${entry.endTime}`;
    grouped.set(key, {
      shopId,
      shopName: resolvedShopName,
      ...entry,
      peopleEntries: entry.peopleEntries.map((p) => ({ ...p })),
    });
  });

  const { shifts, warnings: staffWarnings } = finalizeShifts(grouped, staff);
  const warnings = [...staffWarnings];
  if (usedFallbackShop) {
    warnings.unshift({
      message: `Excel'deki şube adı eşleşmedi; seçili şube kullanıldı: ${resolvedShopName}`,
    });
  }
  const shopIds = shifts.length ? [shopId] : [];
  const weeks = shifts.length ? [weekStart] : [];

  return { shifts, warnings, errors: [], dataRows, shopIds, weeks };
}

function parseMmjMatrixTemplateSheet(rows, { shops, staff, sheetName }) {
  const parsed = readMmjMatrixRows(rows);
  if (!parsed) return null;

  const { templateGrouped, dataRows, errors, weekStart } = parsed;
  if (errors.length) {
    return { shifts: [], warnings: [], errors, dataRows, shopIds: [], weeks: [] };
  }
  if (!shops.length) {
    return {
      shifts: [],
      warnings: [],
      errors: [{ row: 1, message: 'Şube listesi boş — önce Mağazalar\'dan şube ekleyin' }],
      dataRows: 0,
      shopIds: [],
      weeks: [],
    };
  }
  if (!templateGrouped.size) {
    return {
      shifts: [],
      warnings: [],
      errors: [{ row: 1, message: `Vardiya verisi yok: ${sheetName}` }],
      dataRows: 0,
      shopIds: [],
      weeks: [],
    };
  }

  const { shifts, warnings: staffWarnings } = expandTemplateToAllShops(templateGrouped, shops, staff);
  const warnings = [
    {
      message: `Şablon ${shops.length} şubeye uygulandı (${sheetName}). Personel her şubede ayrı eşleştirilir.`,
    },
    ...staffWarnings,
  ];
  const shopIds = shops.map((s) => s.id);
  const weeks = weekStart ? [weekStart] : [];

  return { shifts, warnings, errors: [], dataRows, shopIds, weeks, appliedToAllShops: true };
}

function parseListSheet(rows, { shops, staff }) {
  const headerIdx = rows.findIndex((row) => {
    const mapped = mapHeaders(row);
    return mapped.shop != null && mapped.day != null;
  });
  if (headerIdx < 0) return null;

  const headers = mapHeaders(rows[headerIdx]);
  const grouped = new Map();
  const errors = [];
  let dataRows = 0;

  for (let i = headerIdx + 1; i < rows.length; i += 1) {
    const row = rows[i] || [];
    const shopName = cellText(row[headers.shop]);
    const dayCode = normalizeDayCode(row[headers.day]);
    const peopleCell = headers.people != null ? row[headers.people] : '';
    const weekStart = headers.week != null ? excelDateToIso(row[headers.week]) : null;

    if (!shopName && !dayCode && !cellText(peopleCell)) continue;
    dataRows += 1;

    if (!shopName) {
      errors.push({ row: i + 1, message: 'Şube boş' });
      continue;
    }
    const shopId = findShopId(shopName, shops);
    if (!shopId) {
      errors.push({ row: i + 1, message: `Şube bulunamadı: ${shopName}` });
      continue;
    }
    if (!dayCode) {
      errors.push({ row: i + 1, message: `Geçersiz gün: ${cellText(row[headers.day])}` });
      continue;
    }

    let startTime = '08:00';
    let endTime = '16:30';
    if (headers.start != null || headers.end != null) {
      startTime = parseTimeValue(row[headers.start], startTime);
      endTime = parseTimeValue(row[headers.end], endTime);
    } else if (headers.slot != null) {
      const range = parseShiftTimeRange(row[headers.slot]);
      if (range) {
        startTime = range.start;
        endTime = range.end;
      }
    }

    const effectiveWeek = weekStart || null;
    if (!effectiveWeek) {
      errors.push({ row: i + 1, message: 'Hafta tarihi gerekli' });
      continue;
    }

    const key = `${shopId}|${effectiveWeek}|${dayCode}|${startTime}|${endTime}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        shopId,
        shopName: cellText(shops.find((s) => s.id === shopId)?.shop) || shopName,
        weekStart: effectiveWeek,
        day: dayCode,
        startTime,
        endTime,
        peopleEntries: [],
      });
    }
    splitPeopleNames(peopleCell).forEach((name) => {
      addPersonToShift(grouped.get(key), name, null);
    });
  }

  const { shifts, warnings } = finalizeShifts(grouped, staff);
  const shopIds = [...new Set(shifts.map((s) => s.shopId))];
  const weeks = [...new Set(shifts.map((s) => s.weekStart))];
  return { shifts, warnings, errors, dataRows, shopIds, weeks };
}

/**
 * Parse Excel workbook — supports MMJ matrix (person × day) and list format.
 */
export function parseShiftWorkbook(arrayBuffer, {
  shops = [],
  staff = [],
  fallbackShopId = null,
  applyToAllShops = true,
} = {}) {
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

  const allShifts = [];
  const allWarnings = [];
  const allErrors = [];
  const shopIds = new Set();
  const weeks = new Set();
  let dataRows = 0;
  let sheetName = workbook.SheetNames[0] || '';
  let appliedToAllShops = false;

  if (applyToAllShops) {
    for (const name of workbook.SheetNames) {
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
        header: 1,
        defval: '',
        raw: true,
      });
      if (!rows.length || !isMmjMatrixSheet(rows)) continue;

      const result = parseMmjMatrixTemplateSheet(rows, { shops, staff, sheetName: name });
      if (!result) continue;
      if (result.errors.length) {
        allErrors.push(...result.errors);
        break;
      }
      if (!result.shifts.length) continue;

      sheetName = name;
      allShifts.push(...result.shifts);
      allWarnings.push(...result.warnings);
      dataRows += result.dataRows;
      result.shopIds.forEach((id) => shopIds.add(id));
      result.weeks.forEach((w) => weeks.add(w));
      appliedToAllShops = true;
      break;
    }
  }

  if (!allShifts.length && !allErrors.length) {
    workbook.SheetNames.forEach((name) => {
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
        header: 1,
        defval: '',
        raw: true,
      });
      if (!rows.length) return;

      let result = null;
      if (isMmjMatrixSheet(rows)) {
        result = parseMmjMatrixSheet(rows, { shops, staff, sheetName: name, fallbackShopId });
      } else {
        result = parseListSheet(rows, { shops, staff });
      }
      if (!result || (!result.shifts.length && !result.errors.length)) return;

      if (result.shifts.length) sheetName = name;
      allShifts.push(...result.shifts);
      allWarnings.push(...result.warnings);
      allErrors.push(...result.errors);
      dataRows += result.dataRows;
      result.shopIds.forEach((id) => shopIds.add(id));
      result.weeks.forEach((w) => weeks.add(w));
    });
  }

  if (!allShifts.length && !allErrors.length) {
    throw new Error(
      'Vardiya tablosu bulunamadı. MMJ formatında GÖREVİ satırı ve saat aralıkları (08.00-16.30) bekleniyor.',
    );
  }

  return {
    sheetName,
    appliedToAllShops,
    format: isMmjMatrixSheet(
      XLSX.utils.sheet_to_json(workbook.Sheets[sheetName] || {}, { header: 1, defval: '' }),
    )
      ? 'mmj-matrix'
      : 'list',
    shifts: allShifts,
    warnings: allWarnings,
    errors: allErrors,
    stats: {
      dataRows,
      shifts: allShifts.length,
      shops: shopIds.size,
      weeks: weeks.size,
    },
    shopIds: [...shopIds],
    weeks: [...weeks],
  };
}

export function buildShiftInsertRows({ shifts, userId, sortOrderStart = 0 }) {
  return shifts.map((s, i) => ({
    user_id: userId,
    shop_id: s.shopId,
    day: s.day,
    start_time: s.startTime,
    end_time: s.endTime,
    people: s.people,
    assigned_user_ids: s.assigned_user_ids,
    week_start: s.weekStart,
    sort_order: sortOrderStart + i,
  }));
}

function formatTimeForExcel(time) {
  return String(time || '').replace(/:/g, '.');
}

function roleLabelForStaff(member) {
  return excelDutyLabelForStaff(member);
}

export function buildMmjMatrixSheet({ shop, weekStart, staff = [], existingShifts = [] }) {
  const shopName = cellText(shop?.shop) || 'Mağaza';
  const title = `MMJ ${shopName.toUpperCase()} HAFTALIK SHIFT`;
  const monday = weekStart || mondayIsoFromDate(isoFromParts(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    new Date().getDate(),
  ));

  const dayHeaders = ['PAZARTESİ', 'SALI', 'ÇARŞAMBA', 'PERŞEMBE', 'CUMA', 'CUMARTESİ', 'PAZAR'];
  const dateRow = ['MAĞAZA ADI', 'Tarih'];
  dayHeaders.forEach((_, i) => {
    dateRow.push(addDaysIso(monday, i));
  });

  const dutyRow = [shopName, 'GÖREVİ', ...dayHeaders];

  const personMap = new Map();

  if (existingShifts.length) {
    existingShifts.forEach((shift) => {
      const day = normalizeDayCode(shift.day);
      if (!day) return;
      const timeStr = `${formatTimeForExcel(shift.start_time)}-${formatTimeForExcel(shift.end_time)}`;
      (shift.people || []).forEach((name) => {
        const key = normalizeMatchKey(name);
        if (!personMap.has(key)) {
          const member = staff.find((s) => normalizeMatchKey(s.full_name) === key);
          personMap.set(key, {
            name: cellText(member?.full_name || name),
            role: roleLabelForStaff(member),
            days: {},
          });
        }
        personMap.get(key).days[day] = timeStr;
      });
    });
  } else {
    staff.slice(0, 4).forEach((member) => {
      personMap.set(normalizeMatchKey(member.full_name), {
        name: cellText(member.full_name),
        role: roleLabelForStaff(member),
        days: {},
      });
    });
  }

  const rows = [
    ['', title, '', '', '', '', '', '', '', '', '16.30-01.00'],
    ['', '', '', 'SİPARİŞ', '', '', '', 'SİPARİŞ', '', '15.30-00.00'],
    [...dateRow, '', '08.00-16.30'],
    [...dutyRow, '', '11.00-19.30'],
  ];

  personMap.forEach((person) => {
    const dataRow = [person.name, person.role];
    DAY_CODES.forEach((day) => {
      dataRow.push(person.days[day] || '');
    });
    rows.push(dataRow);
  });

  if (personMap.size === 0) {
    rows.push(['AD SOYAD', 'MD', '12.00-20.30', 'İZİNLİ', '15.30-00.00', '', '', '', '']);
    rows.push(['AD SOYAD', 'VM', '08.00-16.30', '15.30-00.00', '', '', '', '', '']);
    rows.push(['AD SOYAD', 'Barista', '15.30-00.00', '08.00-16.30', '', '', '', '', '']);
  }

  return rows;
}

export function buildShiftTemplateWorkbook({ shops = [], weekStart, shopId, staff = [], existingShifts = [] }) {
  const workbook = XLSX.utils.book_new();
  const targets = shopId != null
    ? shops.filter((s) => s.id === shopId)
    : shops;

  const list = targets.length ? targets : [{ id: null, shop: 'Örnek Şube' }];

  list.forEach((shop, idx) => {
    const shopShifts = existingShifts.filter((s) => s.shop_id === shop.id);
    const shopStaff = staff.filter((m) => {
      if (shop.id == null) return true;
      return staffAssignedToShop(m, shop.id);
    });
    const rows = buildMmjMatrixSheet({
      shop,
      weekStart,
      staff: shopStaff,
      existingShifts: shopShifts,
    });
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet['!cols'] = [
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
    ];
    const tab = cellText(shop.shop).slice(0, 28) || `Sube${idx + 1}`;
    XLSX.utils.book_append_sheet(workbook, sheet, tab);
  });

  return workbook;
}

export function downloadShiftTemplate({ shops, weekStart, shopId, staff, existingShifts }) {
  const workbook = buildShiftTemplateWorkbook({
    shops,
    weekStart,
    shopId,
    staff,
    existingShifts,
  });
  const suffix = weekStart || 'sablon';
  XLSX.writeFile(workbook, `vardiya-plani-${suffix}.xlsx`);
}

export { DAY_CODES };
